import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from 'app/store'
import {
  CompanyLimitsRequest,
  getCompanyLimitsAPI,
  getHoldingLimitAPI,
  HoldingLimitRequest
} from 'utils/api'
import { CurrencyCode } from 'utils/constant/country'
import { ErrorData, ErrorState } from 'features/error/errorSlice'

export enum CompanyLimitStatus {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED'
}

interface HoldingLimitData {
  id: string
  budget: {
    currency: CurrencyCode
    limit: number
    balance: number
    usage: number
  }
  status: CompanyLimitStatus
}

interface CompanyLimitData {
  id: number
  name: string
  currency: CurrencyCode
  limit: number
  balance: number
  usage: number
  status: CompanyLimitStatus
}

interface HoldingLimitState {
  isLoading: boolean
  errors: ErrorData
  data: HoldingLimitData
  companyLimit: {
    isLoading: boolean
    errors: ErrorData
    filters: {
      query: string
    }
    itemsPerPage: number
    currentPage: number
    total: number
    data: CompanyLimitData[]
  }
}

export const initialState: HoldingLimitState = {
  isLoading: true,
  errors: null,
  data: {} as HoldingLimitData,
  companyLimit: {
    isLoading: true,
    errors: null,
    filters: {
      query: ''
    },
    total: 0,
    itemsPerPage: 10,
    currentPage: 1,
    data: []
  }
}

export const getCompanyLimits = createAsyncThunk(
  'holdingLimit/getCompanyLimits',
  async (payload: CompanyLimitsRequest, { getState, rejectWithValue }) => {
    try {
      const { filters } = (getState() as RootState).holdingLimit.companyLimit
      payload.query = filters.query
      return await getCompanyLimitsAPI(payload)
    } catch (error) {
      const { status, data } = error.response
      if (data || status) return rejectWithValue({ status, data })
      return error
    }
  },
  {
    condition: (_, { getState }) => {
      const { error } = getState() as {
        error: ErrorState
      }
      if (
        error.status === 429 &&
        Math.floor(Date.now() / 1000) < error.updateAt!
      ) {
        return false
      }
    },
    dispatchConditionRejection: true
  }
)

export const getHoldingLimit = createAsyncThunk(
  'holdingLimit/getHoldingLimit',
  async (payload: HoldingLimitRequest, { rejectWithValue }) => {
    try {
      return await getHoldingLimitAPI(payload)
    } catch (error) {
      const { status, data } = error.response
      if (data || status) return rejectWithValue({ status, data })
      return error
    }
  },
  {
    condition: (_, { getState }) => {
      const { error } = getState() as {
        error: ErrorState
      }
      if (
        error.status === 429 &&
        Math.floor(Date.now() / 1000) < error.updateAt!
      ) {
        return false
      }
    },
    dispatchConditionRejection: true
  }
)

export const holdingLimitSlice = createSlice({
  name: 'holdingLimit',
  initialState,
  reducers: {
    setFilterQuery: (state, action) => {
      state.companyLimit.filters = {
        ...initialState.companyLimit.filters,
        query: action.payload
      }
      state.companyLimit.currentPage = initialState.companyLimit.currentPage
    },
    setCurrentPage: (state, action) => {
      state.companyLimit.currentPage = action.payload
    },
    setItemsPerPage: (state, action) => {
      state.companyLimit.itemsPerPage = action.payload
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyLimits.pending, (state) => {
        state.companyLimit.isLoading = true
      })
      .addCase(getCompanyLimits.fulfilled, (state, action) => {
        const {
          payload: { data }
        } = action
        state.companyLimit.isLoading = false
        state.companyLimit.data = data.companies
        state.companyLimit.total = data.total
      })
      .addCase(
        getCompanyLimits.rejected,
        (state, action: PayloadAction<any, string, any, any>) => {
          const { payload, error } = action
          state.companyLimit.isLoading = false
          state.companyLimit.errors = payload || {
            status: 500,
            data: `${error.name}: ${error.message}`
          }
        }
      )
      .addCase(getHoldingLimit.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getHoldingLimit.fulfilled, (state, action) => {
        state.isLoading = false
        state.data = action.payload.data
      })
      .addCase(
        getHoldingLimit.rejected,
        (state, action: PayloadAction<any, string, any, any>) => {
          const { payload, error } = action
          state.isLoading = false
          state.errors = payload || {
            status: 500,
            data: `${error.name}: ${error.message}`
          }
        }
      )
  }
})

export const {
  resetState,
  setCurrentPage,
  setFilterQuery,
  setItemsPerPage
} = holdingLimitSlice.actions

export const holdingLimitSelector = (state: RootState) => state.holdingLimit

export const companyLimitSelector = (state: RootState) =>
  state.holdingLimit.companyLimit

export default holdingLimitSlice.reducer
